// components/modals/ConfirmModal.jsx
import React from "react";
import { Modal, Typography } from "antd";
import PrimaryButton from "../Forms/PrimaryButton";

const { Title } = Typography;

/**
 * ACTION_CONFIG:
 * - title: (ctx) => string | JSX
 * - description: (ctx) => string | JSX | null
 * - confirmText: (ctx) => string
 */
const ACTION_CONFIG = {
  BLOCK_CLIENT: {
    title: ({ record }) =>
      ` ${record?.is_blocked ? "Unblock Customer" : "Block Customer"}`,
    description: ({ record }) => (
      <>
        Do you want to {record?.is_blocked ? "unblock" : "block"}{" "}
        <span className="font-bold"> {record?.company_name}</span>?
      </>
    ),
    confirmText: ({ record }) => (record?.is_blocked ? "Unblock" : "Block"),
  },

  BLOCK_USER: {
    title: ({ record }) =>
      ` ${record?.is_blocked ? "Unblock User" : "Block User"}`,
    description: ({ record }) => (
      <>
        Do you want to {record?.is_blocked ? "unblock" : "block"}{" "}
        <span className="font-bold"> {record?.full_name}</span>?
      </>
    ),
    confirmText: ({ record }) => (record?.is_blocked ? "Unblock" : "Block"),
  },

  DELETE_USER: {
    title: () => "Delete User",
    description: ({ record }) => (
      <>
        Do you want to delete{" "}
        <span className="font-bold">{record?.full_name}</span> from{" "}
        <span className="font-bold">Users</span>?
      </>
    ),
    confirmText: () => "Delete",
  },

  ALLOW_EDIT: {
    title: ({ record }) =>
      record?.is_edit_allowed ? "Remove Edit Access" : "Allow Edit Access",
    description: ({ assignedSalesPerson }) => (
      <>
        Assigned Salesperson -{" "}
        <span className="font-bold">{assignedSalesPerson}</span>
      </>
    ),
    confirmText: () => "Update Access",
  },

  CHANGE_ROLE: {
    title: () => "Change Role",
    description: ({ record, newRoleLabel }) => (
      <>
        Do you want to change role of{" "}
        <span className="font-bold">{record?.full_name}</span> to{" "}
        <span className="font-bold">{newRoleLabel || "-"}</span>?
      </>
    ),
    confirmText: () => "Change Role",
  },

  DELETE_BROCHURE: {
    title: () => "Delete Brochure",
    description: ({ record }) => (
      <>
        Do you want to delete <span className="font-bold">{record?.name}</span>{" "}
        from <span className="font-bold">Brochures</span>?
      </>
    ),
    confirmText: () => "Delete",
  },

  GENERIC_CONFIRM: {
    title: ({ title }) => title || "Confirm",
    description: ({ description }) => description || null,
    confirmText: ({ confirmText }) => confirmText || "Confirm",
  },
};

export default function ConfirmModal({
  actionKey,
  record = {},
  assignedSalesPerson,
  newRoleLabel,
  open,
  onCancel,
  onConfirm,
  loading = false,
  width,
  zIndex,
  // allow override for content if necessary
  title: overrideTitle,
  description: overrideDescription,
  confirmText: overrideConfirmText,
}) {
  if (!actionKey) return null;

  const config = ACTION_CONFIG[actionKey] || ACTION_CONFIG.GENERIC_CONFIRM;

  const ctx = {
    record,
    assignedSalesPerson,
    newRoleLabel,
    title: overrideTitle,
    description: overrideDescription,
    confirmText: overrideConfirmText,
  };

  const resolvedTitle = config.title(ctx);
  const resolvedDescription = config.description
    ? config.description(ctx)
    : null;
  const resolvedConfirmText = config.confirmText(ctx);

  return (
    <Modal
      open={open}
      footer={null}
      onCancel={onCancel}
      className="form-css-5"
      zIndex={zIndex}
      title={
        <Title level={2} className="font-abhaya !mb-0">
          {resolvedTitle}
        </Title>
      }
      width={width || (actionKey === "CHANGE_ROLE" ? 520 : undefined)}
    >
      {resolvedDescription && (
        <p className="w-full text-left text-base mt-2 mb-4">
          {resolvedDescription}
        </p>
      )}

      <div className="w-full flex justify-center">
        <PrimaryButton
          text={resolvedConfirmText}
          onClick={onConfirm}
          loading={loading}
          className="me-[30px]"
        />
      </div>
    </Modal>
  );
}
